
// Libraries
import { Component, mixins, namespace } from "nuxt-property-decorator";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import LoadingSpinner from "~/components/molecules/display/LoadingSpinner.vue";
import PartnerTile from "~/components/molecules/text/PartnerTile.vue";
import PriceChart from "~/components/organisms/chart/PriceChart.vue";
import PriceTrendChart from "~/components/organisms/chart/PriceTrendChart.vue";
import DataTile from "~/components/organisms/display/DataTile.vue";
import HeatingOilDataDisplay from "~/components/organisms/display/geographySites/HeatingOilDataDisplay.vue";
import SingleDataTile from "~/components/organisms/display/geographySites/SingleDataTile.vue";
import PriceComparisonHistoricalData from "~/components/organisms/display/PriceComparisonHistoricalData.vue";
import OilImportTable from "~/components/organisms/table/OilImportTable.vue";
import { createBreadCrumbsStructuredData } from "~/mixins/breadcrumb";
import GeoPricePageMixin from "~/mixins/geoPricePageMixin";
import {
  formatNumberAsPercentage,
  returnPriceFormatDE,
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/mixins";
import { Descriptions } from "~/operations/messages";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { decodeUrlForPlace } from "~/operations/shared";
import { BreadCrumbDetail } from "~/types/BreadCrumb";
import { HistoricalOilPriceData } from "~/types/HistoricalOilPriceData";
import {
  calculateHistoricalStatistics,
  getHistoricalData,
} from "~/utils/calculateStatistics";

const OilpriceModule = namespace("oilprice");
const FederalstatesModule = namespace("federalstates");

@Component({
  components: {
    DataTile,
    PriceTrendChart,
    PriceChart,
    OilImportTable,
    PriceComparisonHistoricalData,
    PartnerTile,
    BreadCrumb,
    LoadingSpinner,
    SingleDataTile,
    HeatingOilDataDisplay,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
    decodeUrlForPlace,
  },
})
export default class StatesRedirectView extends mixins(GeoPricePageMixin) {
  head() {
    return {
      ...generateMetaTags(this.$route.path, "heating-oil-overview"),

      script: [
        {
          json: {
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(
              this.breadCrumbData,
            ),
          },
          type: "application/ld+json",
        },
      ],
    };
  }

  @OilpriceModule.State("assets")
  assets: any;

  @OilpriceModule.Action("fetchHistoricalOilPrices")
  fetchHistoricalOilPrices: any;

  @OilpriceModule.State("cheapestCities")
  cheapestCities: any;

  @OilpriceModule.State("nationYearStatistics")
  nationYearStatistics: any;

  @OilpriceModule.State("stateAssetsPrice")
  stateAssetsPrice: any;

  @OilpriceModule.State("cheapestCounties")
  cheapestCounties: any;

  @FederalstatesModule.State("topStatePartners")
  topStatePartners: any;

  Descriptions = Descriptions;

  async fetch() {
    await Promise.all([
      this.fetchPriceTrendData(),
      this.fetchCityPriceTrends(),
      this.$store.dispatch("schwarmometer/fetchUserSignal"),
      this.$store.dispatch("schwarmometer/fetchEsyoilSignal", ""),
      this.fetchDistrictPriceTrends(),
      this.fetchCheapestCities(),
      this.fetchCheapestCounties(),
      this.fetchPartnersAndRatingyByZipcode(),
    ]);
  }

  async fetchDistrictPriceTrends() {
    await this.$store.dispatch("oilprice/fetchDistrictPriceTrend");
  }

  async fetchPriceTrendData() {
    await this.$store.dispatch("oilprice/fetchNationalPriceTrend");
  }

  async fetchCheapestCities() {
    await this.$store.dispatch("oilprice/fetchCheapestCities");
  }

  async fetchCheapestCounties() {
    await this.$store.dispatch("oilprice/fetchCheapestCounties");
  }

  async fetchPartnersAndRatingyByZipcode() {
    await this.$store.dispatch(
      "federalstates/fetchPartnersAndRatingyByZipcode",
      "00000",
    );
  }

  async fetchCityPriceTrends() {
    await this.$store.dispatch("oilprice/fetchAverageCityPrices");
  }

  formatCheapestCitiesTable(state) {
    // Group cities to check for duplicate city names
    const cityGroups = state.reduce((acc, city) => {
      if (!acc[city.name]) {
        acc[city.name] = [];
      }
      acc[city.name].push(city);
      return acc;
    }, {});

    // Using shared mixin method
    return this.formatTableRowData(state, {
      sortKey: "price",
      hrefTemplate: (item) =>
        decodeUrlForPlace(`heizoelpreise-${item.name}-${item.zipcode}`),
      differenceCalculation: (item: any) => item.price - item.yesterday,
      includeNumber: true,
      formatName: (item) => {
        // If there are multiple cities with the same name, append zipcode
        if (cityGroups[item.name].length > 1) {
          return `${item.name} (${item.zipcode})`;
        }
        return item.name;
      },
    });
  }

  formatCheapestCountiesTable(state) {
    // Using shared mixin method
    return this.formatTableRowData(state.data ?? [], {
      sortKey: "current",
      hrefTemplate: (item) =>
        `heizoelpreise/bundeslaender/${item.federalStateName.toLowerCase()}/${item.name.toLowerCase()}`,
      differenceCalculation: (item) => item.current - item.previous,
    });
  }

  get nationWideHistoricalData() {
    return calculateHistoricalStatistics(this.assets.data);
  }

  get historicalHighestData(): HistoricalOilPriceData[] {
    return getHistoricalData(this.nationWideHistoricalData, "highest");
  }

  get historicalLowestData(): HistoricalOilPriceData[] {
    return getHistoricalData(this.nationWideHistoricalData, "lowest");
  }

  get breadCrumbData(): BreadCrumbDetail[] {
    return [
      {
        lastItem: true,
        linkUrl: "/heizoelpreise",
        linkValue: "/heizoelpreise",
      },
    ];
  }

  get todaysPrice() {
    return returnPriceFormatDE(this.stateAssetsPrice.currentPrice.value);
  }

  get priceOneWeekAgo(): number | null {
    // Using shared mixin method
    return this.getPriceFromDaysAgo(this.assets.data, 7);
  }

  get percentageDifference(): number | null {
    // Using shared mixin method
    return this.calculatePercentageDifference(
      this.stateAssetsPrice.currentPrice.value,
      this.priceOneWeekAgo,
    );
  }

  get displayText(): string {
    if (this.percentageDifference === null) {
      return "";
    }

    const formattedPercentage = formatNumberAsPercentage(
      Number(this.percentageDifference),
    );

    if (this.stateAssetsPrice.currentPrice.value < this.priceOneWeekAgo) {
      return `Damit kann man Heizöl ${formattedPercentage} günstiger kaufen als in der Vorwoche.`;
    } else {
      return `Damit ist Heizöl ${formattedPercentage} teurer als in der Vorwoche.`;
    }
  }
}
